import { useState, useRef, useEffect } from "react";
import { useNavigate, useLocation, Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Col, Form, Row, Button, Alert } from "react-bootstrap";
import { BsEye, BsEyeSlash } from "react-icons/bs";
import styles from "./forms.module.css";
import useAuth from "../../hooks/useAuth";
import loginImg from "../../assets/img/sections/signIn_logo.png";
import * as Routes from "../../routes/RoutesPathes";
import { ADMIN_DASHBOARD_ROUTE, SIGNUP_ROUTE, REQUEST_PASSWORD_RESET_ROUTE } from "../../routes/RoutesPathes";
import { loginAPI } from "../../services/authService";
import SchoolStorage from "../../utils/localStorage";

// Build an allowlist of "safe" redirect targets: only static routes (no ":param" segments)
// exported from RoutesPathes.js. Any route with a dynamic id (e.g. /students/details/:studentId)
// is automatically excluded, so a stale "from" location can never carry a stale record id
// into a freshly logged-in (possibly different) account.
const SAFE_REDIRECT_ROUTES = Object.values(Routes).filter(
  (value) => typeof value === "string" && !value.includes(":")
);

const isSafeRedirect = (pathname) => {
  if (!pathname) return false;
  return SAFE_REDIRECT_ROUTES.some(
    (safeRoute) => pathname === safeRoute || pathname.startsWith(`${safeRoute}/`)
  );
};

export const Login = () => {
  const { t, i18n } = useTranslation();
  const { setAuth, setSchool } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const requestedFrom = location.state?.from?.pathname;
  const from = isSafeRedirect(requestedFrom) ? requestedFrom : ADMIN_DASHBOARD_ROUTE;
  const emailRef = useRef();
  const errRef = useRef();
  const [email, setEmail] = useState("");
  const [pwd, setPwd] = useState("");
  const [errMsg, setErrMsg] = useState("");
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);

  useEffect(() => {
    emailRef?.current?.focus();
  }, []);

  useEffect(() => {
    setErrMsg("");
  }, [email, pwd]);

  const handleSubmit = async (e) => {
    e.preventDefault();

    try {
      setLoading(true);
      const response = await loginAPI(email, pwd);

      // Set auth data
      const id = response?.data?.user?.id;
      const role = response?.data?.user?.role;
      const firstName = response?.data?.user?.first_name;
      const lastName = response?.data?.user?.last_name;
      const accessToken = response?.data?.access;
      setAuth({ email, id, role, firstName, lastName, accessToken });

      // Set school data
      const schoolId   = response?.data?.nurseries[0]?.id;
      const schoolName = response?.data?.nurseries[0]?.name;
      const schoolLogo = response?.data?.nurseries[0]?.logo;
      setSchool({ id: schoolId, name: schoolName, logo: schoolLogo });
      SchoolStorage.setSchool(schoolId, schoolName , schoolLogo);

      // Navigate where the user comes from (only if it's a safe, non-record-specific route)
      navigate(from, { replace: true });
    } catch (err) {
      if (!err?.response) {
        setErrMsg("No Server Response");
      } else if (err.response?.status === 400) {
        setErrMsg("Missing Email or Password");
      } else if (err.response?.status === 401) {
        if (err.response?.data?.detail) {
          setErrMsg(err.response?.data?.detail);
        } else {
          setErrMsg("Incorrect Email or Password");
        }
      } else {
        setErrMsg("Login failed");
      }
      errRef.current?.focus();
    } finally {
      setLoading(false); // Reset loading regardless of success/failure
    }
  };

  return (
    <section className={styles.formSection}>
      <div className="container">
        <div className={styles.card}>
          <div className={styles.imgBox}>
            <p className={`s-fs-3 ${styles.imgBoxTitle}`}>
              {t("forms.login_form.img_title")}
            </p>
            <img
              src={loginImg}
              alt="A person browsing a website"
              className={styles.image}
            />
          </div>
          <div className={styles.textBox}>
            <p className={`s-fs-3 ${styles.textBoxTitle}`}>
              {t("forms.login_form.form_title")}
            </p>
            <p className={`mb-4 small text-muted ${styles.textBoxDescription}`}>
              {t("forms.login_form.form_body")}
            </p>
            <Form onSubmit={handleSubmit}>
              <Row className="mb-2">
                <Form.Group as={Col} md="12">
                  <Form.Label className={styles.formLabel}>{t("forms.login_form.email")}</Form.Label>
                  <Form.Control
                    className={styles.formInput}
                    type="email"
                    required
                    ref={emailRef}
                    value={email}
                    maxLength={100}
                    onChange={(e) => setEmail(e.target.value)}
                  />
                </Form.Group>
              </Row>

              <Row className="mb-2">
                <Form.Group as={Col} md="12" className="position-relative">
                  <Form.Label className={styles.formLabel}>{t("forms.login_form.password")}</Form.Label>
                  <Form.Control
                    className={styles.formInput}
                    type={showPassword ? "text" : "password"}
                    required
                    maxLength={100}
                    value={pwd}
                    onChange={(e) => setPwd(e.target.value)}
                  />
                  <div
                    onClick={() => setShowPassword((prev) => !prev)}
                    style={{
                      position: "absolute",
                      top: "46px",
                      [i18n.dir() === "ltr" ? "right" : "left"]: "25px",
                      transform: "translateY(-50%)",
                      cursor: "pointer",
                      color: "#666",
                    }}
                  >
                    {showPassword ? <BsEyeSlash color="#333" /> : <BsEye color="#333" />}
                  </div>
                </Form.Group>
              </Row>
              <Button
                className={`${styles.formBtn} btn-secondary mt-2 w-100`}
                type="submit"
                disabled={loading}
              >
                {loading ? t("buttons.login_loading") : t("buttons.login")}
              </Button>
              {errMsg && errRef && <Alert variant="danger" className="mt-3 mb-0">
                <p ref={errRef} className="m-0" style={{fontSize: "1rem"}}>{errMsg}</p>
              </Alert>}
              <div className="mt-2 text-center small">
                <Link to={REQUEST_PASSWORD_RESET_ROUTE} className="s-link s-purple-link">
                  {t("titles.forgot_password")}
                </Link>
              </div>
              <p className="mb-0 text-center small">
                {t("forms.login_form.have_account")}{" "}
                <Link to={SIGNUP_ROUTE} className="s-link s-purple-link">
                  {t("forms.login_form.signup")}
                </Link>
              </p>
            </Form>
          </div>
        </div>
      </div>
    </section>
  );
};